/**
 * ZATCA Phase-1 QR encoder (browser-side, POS).
 * Byte-for-byte equivalent to platform/print-relay/src/zatca-qr.js and
 * platform/saheeh-customer-web/src/lib/zatca.ts.
 */

export interface TlvFields {
  sellerName: string;
  vatNumber: string;
  timestamp: string | Date;
  invoiceTotal: string | number;
  vatTotal: string | number;
}

export interface SellerProfile {
  name: string;
  vatNumber: string;
  region: string | null;
}

function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

function tlvRecord(tag: number, value: Uint8Array): Uint8Array {
  if (value.length > 255) throw new Error(`TLV tag ${tag} too long: ${value.length} bytes`);
  const out = new Uint8Array(2 + value.length);
  out[0] = tag;
  out[1] = value.length;
  out.set(value, 2);
  return out;
}

export function encodeZatcaTlv(f: TlvFields): Uint8Array {
  const ts = f.timestamp instanceof Date ? f.timestamp.toISOString() : String(f.timestamp);
  const records: Uint8Array[] = [
    tlvRecord(1, utf8(f.sellerName ?? '')),
    tlvRecord(2, utf8(f.vatNumber ?? '')),
    tlvRecord(3, utf8(ts)),
    tlvRecord(4, utf8(String(f.invoiceTotal ?? '0.00'))),
    tlvRecord(5, utf8(String(f.vatTotal ?? '0.00'))),
  ];
  const totalLen = records.reduce((s, r) => s + r.length, 0);
  const out = new Uint8Array(totalLen);
  let offset = 0;
  records.forEach((r) => {
    out.set(r, offset);
    offset += r.length;
  });
  return out;
}

export function encodeZatcaTlvBase64(f: TlvFields): string {
  const bytes = encodeZatcaTlv(f);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export function buildOrderZatcaPayload(opts: {
  seller: SellerProfile | null;
  taxMeta?: { zatca_qr?: string } | null;
  grandTotal: number | null;
  taxTotal: number | null;
  createdAt: string | null;
}): string | null {
  if (opts.taxMeta?.zatca_qr) return opts.taxMeta.zatca_qr;
  const seller = opts.seller;
  if (!seller || !seller.vatNumber || (seller.region && seller.region !== 'SA')) return null;
  try {
    return encodeZatcaTlvBase64({
      sellerName: seller.name,
      vatNumber: seller.vatNumber,
      timestamp: opts.createdAt || new Date().toISOString(),
      invoiceTotal: (opts.grandTotal ?? 0).toFixed(2),
      vatTotal: (opts.taxTotal ?? 0).toFixed(2),
    });
  } catch {
    return null;
  }
}
